import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CreateTemplateCard, UpdateTemplateCard } from '../../../reduxToolkit/Cards/CardsApi';
import { clearSearchFilters } from '../../../reduxToolkit/Search/SearchSlice';
import routes from '../../../Routes/routes';
/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */
const FunctionalityCreateTemplate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { searchByFilters } = useSelector((state) => state.SearchSlice);
    const userData = useSelector((state) => state?.authenticatioauthennSlice?.user.data);
    const cardWidth = 305;
    const cardHeight = 462;
    const cardBackgroundColor = 'white';
    const { t } = useTranslation();
    const stageFront = useRef();
    const stageBack = useRef();
    const [stageProperties, setStageProperties] = useState({
        1: {
            orientation: 'vertical',
            width: cardWidth,
            height: cardHeight,
            squares: [],
            ellipsis: [],
            lines: [],
            texts: [],
            resources: [],
            combinedObject: [],
        },
        2: {
            orientation: 'horizontal',
            width: cardWidth,
            height: cardHeight,
            squares: [],
            ellipsis: [],
            lines: [],
            texts: [],
            resources: [],
            combinedObject: [],
        },
    });
    const [currentStage, setCurrentStage] = useState(1);
    const [selectedShape, setSelectedShape] = useState(null);
    const [expanded, setExpanded] = useState('panel1');
    const [activeFontFamily, setActiveFontFamily] = useState('');
    const [fontSize, setFontSize] = useState(0);
    const [templateName, setTemplateName] = useState('');
    const [shapeHeight, setShapeHeight] = useState(0);
    const [shapeWidth, setShapeWidth] = useState(0);
    const [shapeHorizontal, setShapeHorizontal] = useState(0);
    const [shapeVertical, setShapeVertical] = useState(0);
    const [shapeCornerRadius, setShapeCornerRadius] = useState(0);
    const [shapeRadius, setShapeRadius] = useState(0);
    const [shapeStrokeWidth, setShapeStrokeWidth] = useState(0);
    const [shapeAlign, setShapeAlign] = useState('');
    const [shapeFontStyle, setShapeFontStyle] = useState('');
    const [shapeTextDecoration, setShapeTextDecoration] = useState('');
    const [showResources, setShowResources] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState();
    const [emptyFields, setEmptyFields] = useState(false);
    const location = useLocation();
    // The details route reuses this view: the template can only be inspected
    const disabled = location?.pathname?.startsWith(routes.cardDesigner.details);
    // Destructure the stage properties and current stage.
    const { orientation, width: stageWidth, height: stageHeight } = stageProperties[currentStage];
    const stateFromLink = location.state;
    // useEffect to initialize state based on the link passed state and update the template properties when it changes.
    useEffect(() => {
        let state = stateFromLink;
        if (state !== null && state?.itemSelected) {

            let raw = JSON.parse(state.itemSelected.raw);
            setStageProperties(raw);
            setTemplateName(state.itemSelected.name);
            setIsEdit(true);
            setEditId(state.itemSelected.id);
            for (let key in raw) {
                raw[key].combinedObject.forEach((item) => {
                    if (item.liveContent === 'userSelfie') {
                        item.base64 = '/static/media/userDemoImg.1a05a7d972162c340842.png';
                    }
                });
            }
        }
    }, [stateFromLink]);
    // Handle drag start to store necessary data for dragging.
    const handleDragStart = (e, buttonType, base64 = null, resourceId = null, attribute = null) => {
        e.dataTransfer.setData('buttonType', buttonType);
        if (base64) {
            e.dataTransfer.setData('base64', base64);
        }
        if (resourceId) {
            e.dataTransfer.setData('resourceId', resourceId);
        }
        if (attribute) {
            e.dataTransfer.setData('attribute', attribute);
        }
    };
    // Handle shape click and update the selected shape state.
    const handleShapeClick = (shape) => {
        setSelectedShape(shape);
        updateForm(shape);
    };
    // Update form fields based on the selected shape's properties.
    const updateForm = (shape) => {
        if (shape != undefined) {
            if (shape.height === undefined || shape.width === undefined) {
                setShapeHeight('');
                setShapeWidth('');
            } else {
                setShapeHeight((prevHeight) => (shape.height !== undefined ? shape.height : prevHeight));
                setShapeWidth((prevWidth) => (shape.width !== undefined ? shape.width : prevWidth));
            }
            if (shape.width !== undefined && shape.height !== undefined) {
                const newRadius = Math.max(shape.width / 2, shape.height / 2);
                setShapeRadius(newRadius);
            }
            if (shape.strokeWidth === undefined) {
                setShapeStrokeWidth('');
            } else {
                setShapeStrokeWidth((prevRadius) => prevRadius || shape.strokeWidth);
            }
            setFontSize(shape.fontSize);
            setShapeRadius(shape.radius);
            setShapeCornerRadius(shape.cornerRadius);
            setShapeHorizontal(shape.x);
            setShapeVertical(shape.y);
            setActiveFontFamily(shape.fontFamily);
            setShapeAlign(shape.align);
            setShapeFontStyle(shape.fontStyle);
        }
    };

    // Handle changes to properties like height, width, font size,  and update the stage properties.
    const handleChangeProperty = (type, value) => {
        const propertyUpdater = {
            height: setShapeHeight,
            width: setShapeWidth,
            x: setShapeHorizontal,
            y: setShapeVertical,
            radius: setShapeRadius,
            cornerRadius: setShapeCornerRadius,
            strokeWidth: setShapeStrokeWidth,
            fontFamily: setActiveFontFamily,
            fontSize: setFontSize,
            align: setShapeAlign,
            textDecoration: setShapeTextDecoration,
            fontStyle: setShapeFontStyle,
        };
        const maxValues = {
            width: 305,
            height: 462,
            x: 279,
            y: 436,
        };
        if (type in maxValues && value > maxValues[type]) {
            value = maxValues[type];
        }
        if (type === 'x' || type === 'y') {
            value = Math.round(value);
        }
        if (type === 'x' && value < 0) {
            value = Math.round(0);
        }
        if (type === 'x' && value > 279) {
            value = Math.round(279);
        }
        if (type === 'y' && value < 0) {
            value = Math.round(0);
        }

        if (type === 'y' && value > 436) {
            value = Math.round(436);
        }
        const updater = propertyUpdater[type];
        if (updater) {
            updater(value);
        }
        setStageProperties((prevProperties) => {
            const currentProperties = prevProperties[currentStage];
            const updatedCombinedObject = currentProperties.combinedObject.map((shape, index) => {
                if (shape.id === selectedShape.id) {
                    let updatedShape = {
                        ...shape,
                        [type]: value,
                    };
                    if (type === 'fontStyle' || type === 'textDecoration') {
                        updatedShape = { ...shape };

                        updatedShape = {
                            ...shape,
                            [type]: updateTextProperty(shape[type], value),
                        };
                    }
                    setSelectedShape(updatedShape);
                    return updatedShape;
                } else {
                    return shape;
                }
            });
            return {
                ...prevProperties,
                [currentStage]: {
                    ...currentProperties,
                    combinedObject: updatedCombinedObject,
                },
            };
        });
    };
    // Update text properties like font style or text decoration.
    const updateTextProperty = (currentFontStyle, newValue) => {
        if (currentFontStyle.includes(newValue)) {
            return currentFontStyle.replace(newValue, '').trim();
        } else {
            return [currentFontStyle, newValue].join(' ');
        }
    };
    // Handle template creation.
    const handleCreate = () => {
        if (templateName === '') {
            toast.warn(t('please_fill_all_fields'));
            setEmptyFields(true);
        } else {
            const updatedState = stageProperties;
            for (const key in updatedState) {
                if (updatedState[key].hasOwnProperty('combinedObject')) {
                    updatedState[key].combinedObject.forEach((combinedItem) => {
                        if (combinedItem.property === 'resources') {
                            delete combinedItem.base64;
                        }
                    });
                }
                if (updatedState[key].hasOwnProperty('resources')) {
                    updatedState[key].resources.forEach((resource) => {
                        if (resource.property === 'resources') {
                            delete resource.base64;
                        }
                    });
                }
            }
            const body = {
                name: templateName,
                whoCreatedId: userData?.id,
                raw: JSON.stringify(updatedState),
            };
            dispatch(CreateTemplateCard(body)).then(
                ({
                    payload: {
                        data: { data },
                    },
                }) => {
                    if (data !== null) {
                        dispatch(clearSearchFilters());
                        toast.success(t('has_been_created_successfully'));
                        navigate('/dashboard/employee/card-designer');
                    }
                },
            );
        }
    };
    // Handle template edit.
    const handleEdit = () => {
        if (templateName === '') {
            setEmptyFields(true);
        } else {
            const updatedState = stageProperties;
            for (const key in updatedState) {
                if (updatedState[key].hasOwnProperty('combinedObject')) {
                    updatedState[key].combinedObject.forEach((combinedItem) => {
                        if (combinedItem.property === 'resources') {
                            delete combinedItem.base64;
                        }
                    });
                }
                if (updatedState[key].hasOwnProperty('resources')) {
                    updatedState[key].resources.forEach((resource) => {
                        if (resource?.property === 'resources') {
                            delete resource.base64;
                        }
                    });
                }
            }
            const body = {
                name: templateName,
                whoCreatedId: userData?.id,
                raw: JSON.stringify(updatedState),
                id: editId,
            };
            dispatch(UpdateTemplateCard(body)).then(
                ({
                    payload: {
                        data: { data },
                    },
                }) => {
                    if (data !== null) {
                        navigate(routes.cardDesigner.list);
                        toast.success(t('has_been_updated_successfully').toUpperCase());
                    }
                },
            );
        }
    };
    // Filter and prepare the resources list for rendering.
    const resourcesList = (searchByFilters?.content || [])
        .filter((item) => item?.image !== null)
        .map((item) => ({
            src: `data:image/png;base64,${item?.image}`,
            id: item.id,
        }));

    return {
        templateName,
        setTemplateName,
        showResources,
        resourcesList,
        handleDragStart,
        selectedShape,
        shapeHeight,
        shapeWidth,
        shapeHorizontal,
        shapeVertical,
        shapeCornerRadius,
        shapeRadius,
        shapeStrokeWidth,
        fontSize,
        handleChangeProperty,
        setStageProperties,
        currentStage,
        setSelectedShape,
        isEdit,
        handleEdit,
        handleCreate,
        emptyFields,
        setCurrentStage,
        cardWidth,
        cardHeight,
        stageProperties,
        stageWidth,
        stageHeight,
        handleShapeClick,
        setShapeHeight,
        setShapeWidth,
        setShapeRadius,
        setFontSize,
        cardBackgroundColor,
        stageFront,
        stageBack,
        setShowResources,
        setShapeVertical,
        updateForm,
        setShapeHorizontal,
        expanded,
        setExpanded,
        disabled,
    };
};
export default FunctionalityCreateTemplate;
